import React, { useState, useRef } from 'react';
import { FormControl, Spinner } from 'react-bootstrap';
import { DropdownPortal } from '../common/Portal/DropdownPortal';
import { searchSongs } from '../../services/songSelectionsService';
import styles from './SongSearchInput.module.css';
import { SongData } from '../../../types/planningTypes';

interface SongVersion {
  version_id: number;
  version_title: string;
  is_default?: boolean;
}

interface SongSearchResult {
  song_id: number;
  title: string;
  composer_name?: string;
  lyricist_name?: string;
  versions: SongVersion[];
}

interface SongSearchInputProps {
  onSelect: (songData: SongData) => void;
  defaultValue?: string;
}

export const SongSearchInput: React.FC<SongSearchInputProps> = ({
  onSelect,
  defaultValue = ''
}) => {
  const [searchTerm, setSearchTerm] = useState(defaultValue);
  const [searchResults, setSearchResults] = useState<SongSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = async (term: string) => {
    if (term.length <= 1) {
      setSearchResults([]);
      setDropdownOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await searchSongs(term);
      setSearchResults(results.songs);
      setDropdownOpen(true);
    } catch (error) {
      console.error('Error fetching songs:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
    handleSearch(value);
  };

  const handleSelect = (songId: number, versionId?: number) => {
    const selectedSong = searchResults.find(song => song.song_id === songId);
    if (selectedSong) {
      // Auto-select default version if no version specified and versions exist
      let effectiveVersionId = versionId;
      let selectedVersion = versionId
        ? selectedSong.versions?.find(v => v.version_id === versionId)
        : undefined;

      // Ensure versions array exists and has items
      const versions = selectedSong.versions || [];

      // If no version specified, auto-select the default version OR first version
      if (!effectiveVersionId && versions.length > 0) {
        // First try to find default version
        const defaultVersion = versions.find(v => v.is_default);
        if (defaultVersion) {
          effectiveVersionId = defaultVersion.version_id;
          selectedVersion = defaultVersion;
        } else {
          // Fallback: use first version if no default is set
          const firstVersion = versions[0];
          effectiveVersionId = firstVersion.version_id;
          selectedVersion = firstVersion;
          console.warn(`[SongSearchInput] No default version for song ${songId}, using first version: ${firstVersion.version_id}`);
        }
      }

      // Debug logging to help trace issues
      console.log('[SongSearchInput] handleSelect:', {
        songId,
        requestedVersionId: versionId,
        effectiveVersionId,
        versionsCount: versions.length,
        hasDefault: versions.some(v => v.is_default)
      });

      setSearchTerm(selectedSong.title);
      onSelect({
        song_id: selectedSong.song_id,
        version_id: effectiveVersionId,
        song_title: selectedSong.title,
        composer_name: selectedSong.composer_name,
        lyricist_name: selectedSong.lyricist_name,
        version_title: selectedVersion?.version_title
      });
      setDropdownOpen(false);
    }
  };

  return (
    <div className={styles.songSearchInput}>
      <FormControl
        ref={inputRef}
        type="text"
        placeholder="Search for songs..."
        value={searchTerm}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => searchTerm.length > 1 && setDropdownOpen(true)}
        className={styles.input}
      />
      {isLoading && (
        <div className={styles.spinner}>
          <Spinner animation="border" size="sm" />
        </div>
      )}
      <DropdownPortal
        isOpen={dropdownOpen && searchResults.length > 0}
        triggerRef={inputRef}
        onClose={() => setDropdownOpen(false)}
      >
        <div className={styles.dropdownContent}>
          {searchResults.map((song) => (
            <React.Fragment key={song.song_id}>
              <div
                className={styles.dropdownItem}
                onClick={() => handleSelect(song.song_id)}
              >
                <strong>{song.title}</strong>
                {(song.composer_name || song.lyricist_name) && (
                  <div className={styles.songMeta}>
                    {song.composer_name && `Composer: ${song.composer_name}`}
                    {song.composer_name && song.lyricist_name && ' • '}
                    {song.lyricist_name && `Lyricist: ${song.lyricist_name}`}
                  </div>
                )}
              </div>
              {song.versions.map((version) => (
                <div
                  key={version.version_id}
                  className={`${styles.dropdownItem} ${styles.versionItem} ${version.is_default ? styles.defaultVersion : ''}`}
                  onClick={() => handleSelect(song.song_id, version.version_id)}
                >
                  {`Version: ${version.version_title}`}
                  {version.is_default && <span className={styles.defaultBadge}>Default</span>}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </DropdownPortal>
    </div>
  );
};

export default SongSearchInput;